import { useEffect, useState } from "react";
import moment from "moment";
import Unauthorized from "../../Components/custom/Unauthrized";
import { FaFilter, FaTimes } from "react-icons/fa";
import DashboardItem from "../../Components/dashboard/DashboardItem";
import PieChart from "../../Components/dashboard/PieChart";
import UserDataModal from "../../Components/dashboard/UserDataModal";
import CoursesDataModal from "../../Components/dashboard/CoursesDataModal";
import AssignmentsDataModal from "../../Components/dashboard/AssignmentsDataModal";
import { useFetch, useMutate, buildFilterPayload } from "../../hooks/queries";
import Filter from "../../Components/custom/Filter";

const Dashboard = (props) => {
  const [startDate, setStartDate] = useState(
    moment().subtract(7, "d").format("YYYY-MM-DD"),
  );
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));

  const [selectedSchool, setSelectedSchool] = useState({
    label: "All Schools",
    value: 0,
  });

  const [selectedOperator, setSelectedOperator] = useState(null);
  const [showFilter, setShowFilter] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    userType: "students", // "students" or "teachers"
    statusFilter: "logged", // "logged" or "notLogged" or "all"
  });

  // Courses Modal state
  const [showCoursesModal, setShowCoursesModal] = useState(false);
  const [coursesStatusFilter, setCoursesStatusFilter] = useState("published"); // "published", "unpublished", or "all"

  // Assignments Modal state
  const [showAssignmentsModal, setShowAssignmentsModal] = useState(false);
  const [assignmentsModalConfig, setAssignmentsModalConfig] = useState({
    modalType: "assignmentsVsOutcomes", // "assignmentsVsOutcomes", "submissions", "outcomesLinked"
    statusFilter: "all", // varies by modalType
  });

  const defaultCanvasUsers = {
    studentsCount: 0,
    loggedStudentsCount: 0,
    neverLoggedStudents: 0,
    teachersCount: 0,
    loggedTeachersCount: 0,
    neverLoggedTeachers: 0,
    totalPublishedCourses: 0,
    totalCourses: 0,
  };

  const defaultAssignments = {
    allAssignmentsCount: 0,
    gradedAssignments: 0,
    learningOutcomeCount: 0,
    assignmentsOutcomeCount: 0,
    assignmentsWithLearningOutcomes: 0,
    totalAssignments: 0,
    submittedAssignments: 0,
    learningOutcomesLinkedToAssignments: 0,
  };

  const filters = {
    startDate,
    endDate,
    selectedSchool,
    selectedOperator,
  };

  const {
    data: canvasUsersData,
    isPending: canvasPending,
    isLoading: canvasLoading,
    isFetching: canvasFetching,
    refetch: refetchCanvasUsers,
  } = useFetch({
    queryKey: ["canvasUsers"],
    url: process.env.REACT_APP_API_URL + "CanvasUsers/GetCanvasUsers",
    method: "PATCH",
    data: buildFilterPayload(filters),
    enabled: false,
  });

  const canvasUsers = canvasUsersData || defaultCanvasUsers;

  const {
    data: assignmentsData,
    isPending: assignmentsPending,
    isLoading: assignmentsLoading,
    isFetching: assignmentsFetching,
    refetch: refetchAssignments,
  } = useFetch({
    queryKey: ["assignmentsDashboard"],
    url:
      process.env.REACT_APP_API_URL +
      "Assignment/GetAssignmentsWithLearningOutcomes",
    method: "PATCH",
    data: buildFilterPayload(filters),
    enabled: false,
  });

  const assignments = assignmentsData || defaultAssignments;

  const exportMutation = useMutate({
    mutationKey: ["exportCanvasUsers"],
    url: process.env.REACT_APP_API_URL + "CanvasUsers/ExportCanvasUsersReport",
    method: "PATCH",
    axiosConfig: {
      responseType: "arraybuffer",
      timeout: 60000,
    },
    onSuccess: (data) => {
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      if (navigator.msSaveOrOpenBlob) {
        navigator.msSaveBlob(blob, "ExportDashboardData.xlsx");
      } else {
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute("download", "ExportDashboardData.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
  });

  const exporting = exportMutation.isPending || exportMutation.isLoading;

  const isLoadingCanvasUsers = canvasPending || canvasLoading || canvasFetching;
  const isLoadingAssignments =
    assignmentsPending || assignmentsLoading || assignmentsFetching;
  const anyBusy = isLoadingCanvasUsers || isLoadingAssignments;

  const handleFilterApply = ({ operator, school }) => {
    const newOperator = operator || {
      label: "N/A",
      value: 0,
    };
    const newSchool = school || {
      label: "All Schools",
      value: 0,
    };

    setSelectedOperator(newOperator);
    setSelectedSchool(newSchool);

    // Explicitly trigger refetch when Apply Filter is clicked
    // Use setTimeout to ensure state updates are processed first
    // This ensures filter applies correctly with all current values including dates
    setTimeout(() => {
      refetchCanvasUsers();
      refetchAssignments();
    }, 10);
  };

  // Modal handlers
  const handleOpenModal = (userType, statusFilter) => {
    setModalConfig({ userType, statusFilter });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Courses Modal handlers
  const handleOpenCoursesModal = (statusFilter) => {
    setCoursesStatusFilter(statusFilter);
    setShowCoursesModal(true);
  };

  const handleCloseCoursesModal = () => {
    setShowCoursesModal(false);
  };

  // Assignments Modal handlers
  const handleOpenAssignmentsModal = (modalType, statusFilter) => {
    setAssignmentsModalConfig({ modalType, statusFilter });
    setShowAssignmentsModal(true);
  };

  const handleCloseAssignmentsModal = () => {
    setShowAssignmentsModal(false);
  };

  useEffect(() => {
    refetchCanvasUsers();
    refetchAssignments();
  }, [
    refetchCanvasUsers,
    refetchAssignments,
    selectedSchool,
    selectedOperator,
  ]);

  return (
    <>
      {props.userPermissions.viewDashboard ? (
        <div
          className={`content dashboard ${
            showFilter ? "openFilter" : "closeFilter"
          }`}
        >
          <div className="tool-bar">
            <div className="row d-md-none">
              <div className="col-md-12 text-right">
                <button
                  type="button"
                  className="btn btn-link p-0 close"
                  onClick={() => setShowFilter(false)}
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            <Filter
              isLoading={anyBusy}
              onApply={handleFilterApply}
              showDateRange={true}
              startDate={startDate}
              endDate={endDate}
              onChangeStartDate={setStartDate}
              onChangeEndDate={setEndDate}
              showExport={true}
              onExport={(filterData) => {
                const school = filterData?.school || selectedSchool;
                const operator = filterData?.operator || selectedOperator;
                const exportFilters = {
                  startDate,
                  endDate,
                  selectedSchool: school,
                  selectedOperator: operator,
                };
                exportMutation.mutate(buildFilterPayload(exportFilters));
              }}
              exporting={exporting}
            />
          </div>

          <div className="left-side">
            <div className="row">
              <div className="col-md-12 text-right d-md-none">
                <button
                  className="btn btn-primary"
                  onClick={() => setShowFilter(true)}
                >
                  <FaFilter /> Filter
                </button>
              </div>

              <div className="col-xl-12 col-xxl-4 mb-3 mt-2">
                <DashboardItem
                  title="Students"
                  isLoading={isLoadingCanvasUsers}
                  chartId="studentsChart"
                  logged={canvasUsers.loggedStudentsCount}
                  total={canvasUsers.studentsCount}
                  loggedLabel="Logged In"
                  notLoggedLabel="Not Logged In"
                  onLoggedClick={() => handleOpenModal("students", "logged")}
                  onNotLoggedClick={() =>
                    handleOpenModal("students", "notLogged")
                  }
                  onTotalClick={() => handleOpenModal("students", "all")}
                />
              </div>

              <div className="col-xl-12 col-xxl-4 mb-3 mt-2">
                <DashboardItem
                  title="Teachers"
                  isLoading={isLoadingCanvasUsers}
                  chartId="teachersChart"
                  logged={canvasUsers.loggedTeachersCount}
                  total={canvasUsers.teachersCount}
                  loggedLabel="Logged In"
                  notLoggedLabel="Not Logged In"
                  onLoggedClick={() => handleOpenModal("teachers", "logged")}
                  onNotLoggedClick={() =>
                    handleOpenModal("teachers", "notLogged")
                  }
                  onTotalClick={() => handleOpenModal("teachers", "all")}
                />
              </div>

              <div className="col-xl-12 col-xxl-4 mb-3 mt-2">
                <DashboardItem
                  title="Courses"
                  isLoading={isLoadingCanvasUsers}
                  chartId="coursesChart"
                  logged={canvasUsers.totalPublishedCourses}
                  total={canvasUsers.totalCourses}
                  loggedLabel="Published"
                  notLoggedLabel="Unpublished"
                  onLoggedClick={() => handleOpenCoursesModal("published")}
                  onNotLoggedClick={() => handleOpenCoursesModal("unpublished")}
                  onTotalClick={() => handleOpenCoursesModal("all")}
                />
              </div>

              {/* <div className="col-md-12"> */}
              {/* <hr /> */}
              {/* </div> */}

              <div className="col">
                <DashboardItem
                  title="Assignments VS Learning Outcomes"
                  isLoading={isLoadingAssignments}
                  chartId="assignmentGraph"
                  logged={assignments.assignmentsWithLearningOutcomes}
                  total={assignments.totalAssignments}
                  loggedLabel="Linked to L-Outcomes"
                  notLoggedLabel="Not Linked to L-Outcomes"
                  onLoggedClick={() =>
                    handleOpenAssignmentsModal(
                      "assignmentsVsOutcomes",
                      "linked",
                    )
                  }
                  onNotLoggedClick={() =>
                    handleOpenAssignmentsModal(
                      "assignmentsVsOutcomes",
                      "notLinked",
                    )
                  }
                  onTotalClick={() =>
                    handleOpenAssignmentsModal("assignmentsVsOutcomes", "all")
                  }
                  customGraph={
                    <PieChart
                      chartId="assignmentGraph"
                      logged={assignments.assignmentsWithLearningOutcomes}
                      total={assignments.totalAssignments}
                      loggedLabel="Linked to L-Outcomes"
                      nonLoggedLabel="Not Linked to L-Outcomes"
                    />
                  }
                />
              </div>

              <div className="col">
                <DashboardItem
                  title="Assignments Submissions"
                  isLoading={isLoadingAssignments}
                  chartId="submissionsGraph"
                  logged={assignments.gradedAssignments}
                  total={assignments.submittedAssignments}
                  loggedLabel="Graded"
                  notLoggedLabel="Non Graded"
                  onLoggedClick={() =>
                    handleOpenAssignmentsModal("submissions", "graded")
                  }
                  onNotLoggedClick={() =>
                    handleOpenAssignmentsModal("submissions", "notGraded")
                  }
                  onTotalClick={() =>
                    handleOpenAssignmentsModal("submissions", "all")
                  }
                  customGraph={
                    <PieChart
                      chartId="submissionsGraph"
                      logged={assignments.gradedAssignments}
                      total={assignments.submittedAssignments}
                      loggedLabel="Graded"
                      nonLoggedLabel="Not Graded"
                    />
                  }
                />
              </div>

              <div className="col">
                <DashboardItem
                  title="L-Outcomes Linked To Assignments"
                  isLoading={isLoadingAssignments}
                  chartId="assignmentLGraph"
                  logged={assignments.learningOutcomesLinkedToAssignments}
                  notLogged={assignments.learningOutcomesNotLinkedToAssignments}
                  total={
                    assignments.learningOutcomesLinkedToAssignments +
                    assignments.learningOutcomesNotLinkedToAssignments
                  }
                  loggedLabel="L-Outcomes Linked"
                  notLoggedLabel="Not Linked"
                  onLoggedClick={() =>
                    handleOpenAssignmentsModal("outcomesLinked", "linked")
                  }
                  onNotLoggedClick={() =>
                    handleOpenAssignmentsModal("outcomesLinked", "notLinked")
                  }
                  onTotalClick={() =>
                    handleOpenAssignmentsModal("outcomesLinked", "all")
                  }
                  customGraph={
                    <PieChart
                      chartId="assignmentLGraph"
                      logged={assignments.learningOutcomesLinkedToAssignments}
                      notLogged={
                        assignments.learningOutcomesNotLinkedToAssignments
                      }
                      total={
                        assignments.learningOutcomesLinkedToAssignments +
                        assignments.learningOutcomesNotLinkedToAssignments
                      }
                      loggedLabel="L-Outcomes Linked"
                      nonLoggedLabel="Not Linked"
                    />
                  }
                />
              </div>
            </div>
          </div>

          {/* User Data Modal */}
          <UserDataModal
            show={showModal}
            onHide={handleCloseModal}
            userType={modalConfig.userType}
            statusFilter={modalConfig.statusFilter}
            startDate={startDate}
            endDate={endDate}
            schoolId={selectedSchool?.value || 0}
            operatorId={selectedOperator?.value || 0}
          />

          {/* Courses Data Modal */}
          <CoursesDataModal
            show={showCoursesModal}
            onHide={handleCloseCoursesModal}
            statusFilter={coursesStatusFilter}
            startDate={startDate}
            endDate={endDate}
            schoolId={selectedSchool?.value || 0}
            operatorId={selectedOperator?.value || 0}
          />

          {/* Assignments Data Modal */}
          <AssignmentsDataModal
            show={showAssignmentsModal}
            onHide={handleCloseAssignmentsModal}
            modalType={assignmentsModalConfig.modalType}
            statusFilter={assignmentsModalConfig.statusFilter}
            startDate={startDate}
            endDate={endDate}
            schoolId={selectedSchool?.value || 0}
            operatorId={selectedOperator?.value || 0}
          />
        </div>
      ) : (
        <Unauthorized />
      )}
    </>
  );
};

export default Dashboard;
